/**
 * Shared types and pure helper functions for the Board Sharing Modal.
 *
 * All functions are pure (no side effects), exported, and well-typed.
 *
 * Validates: Requirements 3.7, 5.2, 8.6, 4.2
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type BoardRole = 'admin' | 'normal' | 'read-only';

export interface BoardMemberItem {
  readonly userId: string;
  readonly displayName: string;
  readonly email: string;
  readonly role: BoardRole;
  readonly joinedAt: string;
}

export interface PendingInvitationItem {
  readonly invitationId: string;
  readonly inviteeEmail: string;
  readonly role: BoardRole;
  readonly createdAt: string;
  readonly status: string;
}

export type SelectedUser =
  | { userId: string; displayName: string; email: string }
  | { email: string };

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ROLE_PRIORITY: Record<BoardRole, number> = {
  admin: 0,
  normal: 1,
  'read-only': 2,
};

// ---------------------------------------------------------------------------
// Helper Functions
// ---------------------------------------------------------------------------

/**
 * Filters search results to exclude users who are already board members.
 *
 * Validates: Requirements 3.7
 */
export function filterExistingMembers<T extends { userId: string }>(
  results: readonly T[],
  memberIds: readonly string[],
): T[] {
  const memberIdSet = new Set(memberIds);
  return results.filter((result) => !memberIdSet.has(result.userId));
}

/**
 * Sorts members by role priority (admin > normal > read-only) then
 * alphabetically by display name within each role group.
 *
 * Validates: Requirements 5.2
 */
export function sortMembersByRolePriority(
  members: readonly BoardMemberItem[],
): BoardMemberItem[] {
  if (!members || members.length === 0) return [];
  return [...members].sort((a, b) => {
    const roleDiff = ROLE_PRIORITY[a.role] - ROLE_PRIORITY[b.role];
    if (roleDiff !== 0) return roleDiff;
    return (a.displayName ?? '').localeCompare(b.displayName ?? '');
  });
}

/**
 * Filters invitations to only include those with status "pending".
 *
 * Validates: Requirements 8.6
 */
export function filterPendingInvitations(
  invitations: readonly PendingInvitationItem[],
): PendingInvitationItem[] {
  if (!invitations || invitations.length === 0) return [];
  return invitations.filter((invitation) => invitation.status === 'pending');
}

/**
 * Constructs the correct POST body for creating an invitation.
 * Uses `userId` field for user-based selections, `email` field for email-only selections.
 *
 * Validates: Requirements 4.2
 */
export function buildInvitationPayload(
  selectedUser: SelectedUser,
  role: BoardRole,
): { userId?: string; email?: string; role: string } {
  if ('userId' in selectedUser) {
    return { userId: selectedUser.userId, role };
  }
  return { email: selectedUser.email, role };
}
